import { PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Dropdown, Form, type FormInstance, Spin } from 'antd';
import { useRef } from 'react';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type {
    ProdutoBodyEstoque,
    ProdutoBodyLote,
    ProdutoBodyLoteEstoque,
} from '@/features/produto/types/ProdutoBody';
import { ErrorComponent } from '@/features/erro/components/ErrorComponent';
import { useQueryProdutoLocalEstoqueGet } from '@/features/produto-local-estoque/api/apiProdutoLocalEstoqueGet';
import { ProdutoLocalEstoqueStatus } from '@/features/produto-local-estoque/types/enum';
import { useProdutoUnidadeGetQuery } from '@/features/produto/api/apiProdutoUnidadeGet';
import { ProdutoTipo } from '@/features/produto/types/enum';
import { ProdutoTabEstoqueLocalResumo } from './ProdutoTabEstoqueLocalResumo';
import { ProdutoTabEstoqueLocalTable } from './ProdutoTabEstoqueLocalTable';
import { validateProdutoEstoquePorLocal } from './validateProdutoEstoquePorLocal';

export function ProdutoTabEstoqueLocal({
    form,
    setFormDirty,
}: {
    form: FormInstance<FormProdutoValues>;
    setFormDirty: (dirty: boolean) => void;
}) {
    const estoquesLotesPreservados = useRef(
        new Map<ProdutoBodyLote['key'], Map<number, ProdutoBodyLoteEstoque>>(),
    );

    const produtoEstoque = Form.useWatch('produto_estoque', { form, preserve: true }) || [];

    const estoquesAtivos = produtoEstoque.filter(
        (estoque: ProdutoBodyEstoque) => estoque.excluir !== 'S',
    );

    const estoqueTotal = Form.useWatch('estoque_total', { form, preserve: true });

    const produtoTipo = Form.useWatch('pro_tipo', { form, preserve: true });
    const produtoComVariacao = produtoTipo === ProdutoTipo.ComVariacao;

    const produtoUnidadeIdpk = Form.useWatch('pro_produto_unidade_idpk', form);

    const { data: unidadesData } = useProdutoUnidadeGetQuery();

    const {
        data: localEstoqueData,
        error: localEstoqueError,
        isFetching: localEstoqueFetching,
    } = useQueryProdutoLocalEstoqueGet();

    const locais = localEstoqueData?.registros || [];

    const locaisManuais = locais.filter(
        (local) => local.est_produto_adicionar_automaticamente !== 'S',
    );

    const temLocalAtivoManual = locaisManuais.some(
        (local) => local.est_status === ProdutoLocalEstoqueStatus.Ativado,
    );

    const locaisDisponiveis = locaisManuais.filter(
        (local) =>
            local.est_status === ProdutoLocalEstoqueStatus.Ativado &&
            !estoquesAtivos.some(
                (estoque: ProdutoBodyEstoque) => estoque.pre_estoque_idpk === local.est_idpk,
            ),
    );

    const atualizarEstoques = (estoques: ProdutoBodyEstoque[]): void => {
        const ativos = estoques.filter((estoque) => estoque.excluir !== 'S');
        const lotesAtuais: ProdutoBodyLote[] = form.getFieldValue('lote') ?? [];

        form.setFieldsValue({
            estoque_total: ativos.reduce(
                (total, estoque) => total + (estoque.pre_estoque_atual ?? 0),
                0,
            ),
            lote: lotesAtuais.map((lote) => {
                const preservados =
                    estoquesLotesPreservados.current.get(lote.key) ??
                    new Map<number, ProdutoBodyLoteEstoque>();

                for (const estoque of lote.estoque ?? []) {
                    if (typeof estoque.ple_estoque_idpk === 'number') {
                        preservados.set(estoque.ple_estoque_idpk, estoque);
                    }
                }

                estoquesLotesPreservados.current.set(lote.key, preservados);

                return {
                    ...lote,
                    estoque: ativos.map(
                        (estoque) =>
                            preservados.get(estoque.pre_estoque_idpk) ?? {
                                ple_estoque_atual: 0,
                                ple_estoque_idpk: estoque.pre_estoque_idpk,
                            },
                    ),
                };
            }),
            produto_estoque: estoques,
        });

        setFormDirty(true);
        void form.validateFields(['produto_estoque']).catch(() => undefined);
    };

    const adicionar = (idpk: number): void => {
        if (!locaisDisponiveis.some((local) => local.est_idpk === idpk)) {
            return;
        }

        const valores: ProdutoBodyEstoque[] = form.getFieldValue('produto_estoque') ?? [];
        const existente = valores.find((estoque) => estoque.pre_estoque_idpk === idpk);

        if (existente) {
            atualizarEstoques(
                valores.map((estoque) =>
                    estoque === existente
                        ? {
                              ...estoque,
                              excluir: undefined,
                              pre_estoque_atual: estoque.pre_estoque_atual ?? 0,
                          }
                        : estoque,
                ),
            );
        } else {
            const lotesAtuais: ProdutoBodyLote[] = form.getFieldValue('lote') ?? [];

            const quantidadeLotes = lotesAtuais
                .filter((lote) => lote.excluir !== 'S')
                .reduce((total, lote) => {
                    const estoqueLote =
                        lote.estoque?.find((item) => item.ple_estoque_idpk === idpk) ??
                        estoquesLotesPreservados.current.get(lote.key)?.get(idpk);

                    return total + (estoqueLote?.ple_estoque_atual ?? 0);
                }, 0);

            atualizarEstoques([
                ...valores,
                {
                    pre_estoque_atual: quantidadeLotes,
                    pre_estoque_disponivel: quantidadeLotes,
                    pre_estoque_idpk: idpk,
                    pre_estoque_inicial: 0,
                    pre_estoque_reservado: 0,
                    pre_localizacao: '',
                    pre_padrao: 'N',
                },
            ]);
        }
    };

    const excluir = (index: number): void => {
        const valores: ProdutoBodyEstoque[] = form.getFieldValue('produto_estoque') ?? [];
        const estoque = valores[index];

        if (
            !estoque ||
            !locaisManuais.some((local) => local.est_idpk === estoque.pre_estoque_idpk)
        ) {
            return;
        }

        atualizarEstoques(
            estoque.pre_idpk
                ? valores.map((item, i) => (i === index ? { ...item, excluir: 'S' } : item))
                : valores.filter((_, i) => i !== index),
        );
    };

    const unidade =
        unidadesData?.registros.find((item) => item.pru_idpk === produtoUnidadeIdpk)?.pru_sigla ??
        '';

    if (localEstoqueError) {
        return <ErrorComponent error={localEstoqueError} />;
    }

    return (
        <Spin spinning={localEstoqueFetching}>
            {locais.length === 0 && !localEstoqueFetching && (
                <Alert
                    className='mb-2'
                    showIcon={true}
                    title='Cadastre ao menos um local de estoque antes de salvar o produto.'
                    type='warning'
                />
            )}

            {!produtoComVariacao && temLocalAtivoManual && (
                <div className='mb-2 flex justify-end'>
                    <Dropdown
                        disabled={locaisDisponiveis.length === 0}
                        menu={{
                            items: locaisDisponiveis.map((local) => ({
                                key: local.est_idpk,
                                label: local.est_descricao,
                            })),
                            onClick: ({ key }) => adicionar(Number(key)),
                        }}
                        trigger={['click']}
                    >
                        <Button
                            disabled={locaisDisponiveis.length === 0}
                            icon={<PlusOutlined />}
                            type='primary'
                        >
                            Adicionar
                        </Button>
                    </Dropdown>
                </div>
            )}

            <Form.List
                name='produto_estoque'
                rules={[
                    {
                        validator: async (_, value: ProdutoBodyEstoque[] | undefined) => {
                            const result = validateProdutoEstoquePorLocal(
                                value,
                                form.getFieldValue('lote'),
                                form.getFieldValue('pro_tipo'),
                            );

                            if (result.error) {
                                return Promise.reject(result.error);
                            }
                        },
                    },
                ]}
            >
                {(fields, _, { errors }) => (
                    <>
                        {!produtoComVariacao && (
                            <ProdutoTabEstoqueLocalTable
                                exibirAcoes={temLocalAtivoManual}
                                fields={fields.filter(
                                    (field) => produtoEstoque[field.name]?.excluir !== 'S',
                                )}
                                form={form}
                                locais={locais}
                                onDelete={excluir}
                                produtoEstoque={produtoEstoque}
                                setFormDirty={setFormDirty}
                                unidade={unidade}
                            />
                        )}

                        <Form.ErrorList errors={errors} />

                        {(produtoComVariacao || estoquesAtivos.length > 1) && (
                            <ProdutoTabEstoqueLocalResumo
                                agruparPorLocal={produtoComVariacao}
                                estoqueTotal={produtoComVariacao ? estoqueTotal : undefined}
                                produtoEstoque={estoquesAtivos}
                                quantidadeLocais={estoquesAtivos.length}
                                unidade={unidade}
                            />
                        )}
                    </>
                )}
            </Form.List>
        </Spin>
    );
}
